import { PayloadAction, createSlice } from "@reduxjs/toolkit";
import type { NormalizedList } from "../../pages/Dashboard/useHook";

export interface IDashboardStateSlice {
  riskReportList: NormalizedList<IRiskReportList[0]>;
  ipvNotesList: NormalizedList<IIPVNotesListItem>;
  flagList: {
    data: IFlagAlert[];
    isLoading: boolean;
  };
  flagListReviewed: {
    data: IFlagAlertClinicalInbox[];
    isLoading: boolean;
  };
  flagListMuted: {
    data: IFlagAlertClinicalInbox[];
    isLoading: boolean;
  };
  startTime: {
    ["0"]: number | null;
    ["1"]: number | null;
    ["2"]: number | null;
    ["3"]: number | null;
  };
  graphImages: Dictionary<HTMLCanvasElement | null>;
  residentPendingGraphs: number[];
  dashboardFloor: number[];
  outdatedTabs: string[];

  stats: Dictionary<{ avg: number | null; date: string | null }>;
  residentPendingStats: number[];

  statsHR: Dictionary<{ avg: number | null; date: string | null }>;
  residentPendingStatsHR: number[];

  statsTimeout: number | null;
}

const name = "dashboard";

const initialListState = {
  data: {
    data: [],
    paging: { has_more: true, page: 0, page_size: 20, total_records: 0 },
  },
  isLoading: false,
};

export const initialState: IDashboardStateSlice = {
  riskReportList: initialListState,
  ipvNotesList: initialListState,
  flagList: {
    data: [],
    isLoading: false,
  },
  flagListReviewed: {
    data: [],
    isLoading: false,
  },
  flagListMuted: {
    data: [],
    isLoading: false,
  },
  startTime: {
    "0": null,
    "1": null,
    "2": null,
    "3": null,
  },
  graphImages: {},
  residentPendingGraphs: [],
  dashboardFloor: [],
  outdatedTabs: [],

  stats: {},
  residentPendingStats: [],

  statsHR: {},
  residentPendingStatsHR: [],

  statsTimeout: null,
};

export const dashboardSlice = createSlice({
  name,
  initialState,
  reducers: {
    setFlagList(
      state,
      action: PayloadAction<{
        data: IFlagAlert[];
        isLoading: boolean;
      }>
    ) {
      state.flagList = action.payload;
    },
    setFlagListReviewed(
      state,
      action: PayloadAction<{
        data: IFlagAlertClinicalInbox[];
        isLoading: boolean;
      }>
    ) {
      state.flagListReviewed = action.payload;
    },
    setFlagListMuted(
      state,
      action: PayloadAction<{
        data: IFlagAlertClinicalInbox[];
        isLoading: boolean;
      }>
    ) {
      state.flagListMuted = action.payload;
    },
    setRiskReportList(
      state,
      action: PayloadAction<NormalizedList<IRiskReportList[0]>>
    ) {
      state.riskReportList = action.payload;
      state.outdatedTabs = [
        ...state.outdatedTabs.filter((item) => item !== "1"),
      ];
    },
    setIPVNotesList(
      state,
      action: PayloadAction<NormalizedList<IIPVNotesListItem>>
    ) {
      state.ipvNotesList = action.payload;
      state.outdatedTabs = [
        ...state.outdatedTabs.filter((item) => item !== "2"),
      ];
    },
    setStartTime(state, action: PayloadAction<Record<string, number | null>>) {
      state.startTime = {
        ...state.startTime,
        ...action.payload,
      };
    },
    setGraphImages(
      state,
      action: PayloadAction<{
        value: Dictionary<HTMLCanvasElement | null>;
      }>
    ) {
      const { value } = action.payload;
      const combinedSettings = Object.assign({}, state.graphImages, value);
      state.graphImages = combinedSettings;
    },
    setResidentPendingGraphs(state, action: PayloadAction<number[]>) {
      state.residentPendingGraphs = action.payload;
    },
    removeGraphImage(state, action: PayloadAction<number[] | undefined>) {
      if (!action.payload) {
        state.graphImages = {};
        return;
      }
      const idsToRemove = action.payload;
      const graphImages = { ...state.graphImages };
      idsToRemove.forEach((id) => {
        delete graphImages[id];
      });
      state.graphImages = graphImages;
    },
    setDashboardStateReset(state) {
      return {
        ...initialState,
        dashboardFloor: state.dashboardFloor,
        outdatedTabs: ["0", "1", "2"],
      };
    },
    setStats(
      state,
      action: PayloadAction<{
        value: IDashboardStateSlice["stats"];
        timeout: number;
      }>
    ) {
      state.stats = { ...state.stats, ...action.payload.value };
      state.statsTimeout = action.payload.timeout;
    },
    setResidentPendingStats(state, action: PayloadAction<number[]>) {
      state.residentPendingStats = action.payload;
    },
    removeStats(state, action: PayloadAction<number[] | undefined>) {
      if (!action.payload) {
        state.stats = {};
        return;
      }
      const idsToRemove = action.payload;
      const tempStat = { ...state.stats };
      idsToRemove.forEach((id) => {
        delete tempStat[id];
      });
      state.stats = tempStat;
      state.statsTimeout = null;
    },
    setStatsHR(
      state,
      action: PayloadAction<{
        value: IDashboardStateSlice["stats"];
        timeout: number;
      }>
    ) {
      state.statsHR = { ...state.statsHR, ...action.payload.value };
      state.statsTimeout = action.payload.timeout;
    },
    setResidentPendingStatsHR(state, action: PayloadAction<number[]>) {
      state.residentPendingStatsHR = action.payload;
    },
    removeStatsHR(state, action: PayloadAction<number[] | undefined>) {
      if (!action.payload) {
        state.statsHR = {};
        return;
      }
      const idsToRemove = action.payload;
      const tempStat = { ...state.statsHR };
      idsToRemove.forEach((id) => {
        delete tempStat[id];
      });
      state.statsHR = tempStat;
      state.statsTimeout = null;
    },
    setDashboardFloor(state, action: PayloadAction<number[]>) {
      state.dashboardFloor = action.payload;
    },
    setDashboardOutdateTabs(state, action: PayloadAction<string>) {
      state.outdatedTabs = [
        ...new Set([...state.outdatedTabs, action.payload]),
      ];
    },
    setDashboardOutdateTabsAll(state) {
      state.outdatedTabs = ["0", "1", "2"];
      state.flagList = {
        data: [],
        isLoading: false,
      };
      state.flagListMuted = {
        data: [],
        isLoading: false,
      };
      state.riskReportList = initialListState;
      state.ipvNotesList = initialListState;
      state.stats = {};
      state.statsHR = {};
    },
    setDashboardReset(state) {
      state.riskReportList = initialListState;
      state.ipvNotesList = initialListState;
      state.startTime = {
        "0": null,
        "1": null,
        "2": null,
        "3": null,
      };
    },
  },
});

export const {
  setFlagList,
  setRiskReportList,
  setIPVNotesList,
  setStartTime,
  setDashboardStateReset,
  setGraphImages,
  removeGraphImage,
  setResidentPendingGraphs,
  setStats,
  setResidentPendingStats,
  removeStats,
  setStatsHR,
  setResidentPendingStatsHR,
  removeStatsHR,
  setDashboardFloor,
  setDashboardOutdateTabs,
  setDashboardOutdateTabsAll,
  setDashboardReset,
  setFlagListReviewed,
  setFlagListMuted,
} = dashboardSlice.actions;

export default dashboardSlice.reducer;
